/**
 * Role Scaffold — single source of truth for what each role sees.
 *
 * - sidebarConfig[role]  → grouped sidebar nav
 * - kpiConfig[role]      → list of KPI card descriptors for the dashboard
 * - widgetConfig[role]   → list of dashboard widgets to render
 *
 * Phase 1: scaffold only. KPI/widget content for non-editor roles is stub —
 * Phases 3, 4, 12 fill them in per spec. Sidebar IS final per-spec already.
 *
 * Moderators inherit their department's scaffold (per Phase 1 decision),
 * with extras (Team View / Audit) added based on granted capabilities.
 */

import type { ComponentType } from "react";
import {
  AlertCircle,
  BookUser,
  Briefcase,
  Building2,
  ClipboardList,
  Cog,
  Eye,
  FileText,
  LayoutDashboard,
  ListChecks,
  Package,
  Search,
  TrendingUp,
  Wallet,
  CalendarDays,
  Sparkles,
  KeyRound,
} from "lucide-react";
import type { AppRole, Department } from "@/lib/auth-context";

export type ScaffoldRole =
  | "admin"
  | "hr"
  | "editor"
  | "graphic_designer"
  | "ai"
  | "scriptwriter"
  | "marketer"
  | "seo"
  | "smm"
  | "calling";

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Resolve which scaffold to use given an app role + department. */
export function resolveScaffoldRole(args: {
  appRole: AppRole | null;
  department: Department | null | undefined;
}): ScaffoldRole {
  if (args.appRole === "admin") return "admin";
  // moderator = same scaffold as their department user (Phase 1 decision)
  const dept = args.department ?? "editor";
  if (dept === "admin") return "admin";
  return dept as ScaffoldRole;
}

// ---------- Sidebar configs ----------

// Creative departments (Editors + Graphic Designer) get full review workflow.
const creativeSubmitterMain: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/update-task", label: "Update Task", icon: ClipboardList },
  { to: "/my-submissions", label: "My Submissions", icon: ListChecks },
  { to: "/corrections", label: "Corrections", icon: AlertCircle },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/vault", label: "The Vault", icon: KeyRound },
  { to: "/performance", label: "Performance", icon: TrendingUp },
];

// Non-creative departments only do daily work tracking — no review/correction.
const trackingSubmitterMain: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/update-task", label: "Update Task", icon: ClipboardList },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/vault", label: "The Vault", icon: KeyRound },
  { to: "/performance", label: "Performance", icon: TrendingUp },
];

const creativeSubmitterScaffold = (): NavGroup[] => [{ label: "Main", items: creativeSubmitterMain }];
const trackingSubmitterScaffold = (): NavGroup[] => [{ label: "Main", items: trackingSubmitterMain }];

export const sidebarConfig: Record<ScaffoldRole, NavGroup[]> = {
  admin: [
    { label: "Main", items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/today-tasks", label: "Today's Task", icon: ListChecks },
    ] },
    {
      label: "Review",
      items: [
        { to: "/creatives", label: "Creatives", icon: Sparkles },
        { to: "/admin/assign-task", label: "Assign Task", icon: ClipboardList },
      ],
    },
    {
      label: "Planning",
      items: [
        { to: "/calendar", label: "Calendar", icon: CalendarDays },
        { to: "/vault", label: "The Vault", icon: KeyRound },
      ],
    },
    {
      label: "Team",
      items: [
        { to: "/team/overview", label: "Overview", icon: Eye },
        { to: "/team/departments", label: "By Department", icon: Building2 },
        { to: "/team/employees", label: "By Employee", icon: Search },
      ],
    },
    {
      label: "HR",
      items: [
        { to: "/hr/directory", label: "All Employees", icon: BookUser },
        { to: "/hr/recruitment", label: "Recruitment", icon: Briefcase },
        { to: "/hr/assets", label: "Allot Assets", icon: Package },
        { to: "/hr/leave", label: "Leave Requests", icon: CalendarDays },
        { to: "/hr/payroll", label: "Payroll", icon: Wallet },
        { to: "/hr/exit-reviews", label: "Exit Interviews", icon: FileText },
      ],
    },
    {
      label: "Settings",
      items: [{ to: "/admin/settings", label: "Settings", icon: Cog }],
    },
  ],
  hr: [
    { label: "Main", items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/today-tasks", label: "Today's Task", icon: ListChecks },
    ] },
    {
      label: "HR",
      items: [
        { to: "/hr/directory", label: "All Employees", icon: BookUser },
        { to: "/hr/recruitment", label: "Recruitment", icon: Briefcase },
        { to: "/hr/assets", label: "Allot Assets", icon: Package },
        { to: "/hr/documents", label: "Documents", icon: FileText },
        { to: "/hr/payroll", label: "Salary & Payroll", icon: Wallet },
        { to: "/hr/leave", label: "Leave Management", icon: CalendarDays },
        { to: "/hr/exit-reviews", label: "Exit Interviews", icon: FileText },
        { to: "/calendar", label: "Calendar", icon: CalendarDays },
        { to: "/vault", label: "The Vault", icon: KeyRound },
      ],
    },
  ],
  editor: creativeSubmitterScaffold(),
  graphic_designer: creativeSubmitterScaffold(),
  ai: creativeSubmitterScaffold(),
  scriptwriter: trackingSubmitterScaffold(),
  marketer: trackingSubmitterScaffold(),
  seo: trackingSubmitterScaffold(),
  smm: trackingSubmitterScaffold(),
  calling: trackingSubmitterScaffold(),
};

// ---------- KPI configs (stub for non-editor; Phases 3/4/12 finalize) ----------

export interface KpiDescriptor {
  key: string;
  label: string;
  /** Stub fallback value; real implementations compute live in dashboard.tsx. */
  stub?: string;
  trend?: string;
  variant?: "primary" | "danger" | "default";
}

export const kpiConfig: Record<ScaffoldRole, KpiDescriptor[]> = {
  admin: [
    { key: "active_users", label: "Active Users", stub: "—", variant: "primary" },
    { key: "tasks_today", label: "Tasks Logged Today", stub: "—" },
    { key: "pending_approvals", label: "Pending Approvals", stub: "—" },
    { key: "departments", label: "Departments", stub: "—" },
    { key: "submissions_month", label: "Total Submissions This Month", stub: "—" },
  ],
  hr: [
    { key: "active_employees", label: "Active Employees", stub: "—", variant: "primary" },
    { key: "open_positions", label: "Open Positions", stub: "—" },
    { key: "pending_onboarding", label: "Pending Onboarding", stub: "—" },
    { key: "documents_pending", label: "Documents Pending", stub: "—" },
    { key: "todays_leaves", label: "Today's Leaves", stub: "—" },
  ],
  editor: [
    { key: "videos_month", label: "Videos This Month", variant: "primary" },
    { key: "total_points", label: "Total Points" },
    { key: "corrections", label: "Corrections", variant: "danger" },
    { key: "approval_rate", label: "Approval Rate" },
    { key: "team_rank", label: "Team Rank" },
  ],
  graphic_designer: [
    { key: "creatives_month", label: "Creatives This Month", variant: "primary" },
    { key: "corrections", label: "Corrections", variant: "danger" },
    { key: "approval_rate", label: "Approval Rate" },
    { key: "team_rank", label: "Team Rank" },
  ],
  ai: [
    { key: "creatives_month", label: "Creatives This Month", variant: "primary" },
    { key: "corrections", label: "Corrections", variant: "danger" },
    { key: "approval_rate", label: "Approval Rate" },
    { key: "team_rank", label: "Team Rank" },
  ],
  scriptwriter: [
    { key: "scripts_month", label: "Scripts This Month", variant: "primary" },
    { key: "corrections", label: "Corrections", variant: "danger" },
    { key: "approval_rate", label: "Approval Rate" },
    { key: "team_rank", label: "Team Rank" },
  ],
  marketer: [
    { key: "tasks_month", label: "Tasks This Month", variant: "primary" },
    { key: "corrections", label: "Corrections", variant: "danger" },
    { key: "approval_rate", label: "Approval Rate" },
    { key: "team_rank", label: "Team Rank" },
  ],
  seo: [
    { key: "tasks_month", label: "Tasks This Month", variant: "primary" },
    { key: "corrections", label: "Corrections", variant: "danger" },
    { key: "approval_rate", label: "Approval Rate" },
    { key: "team_rank", label: "Team Rank" },
  ],
  smm: [
    { key: "tasks_month", label: "Tasks This Month", variant: "primary" },
    { key: "outreached", label: "Outreached" },
    { key: "finalized", label: "Finalized" },
    { key: "deliverables", label: "Deliverables" },
  ],
  calling: [
    { key: "calls_month", label: "Calls This Month", variant: "primary" },
    { key: "conversions", label: "Conversions" },
    { key: "revenue", label: "Revenue Recovered ₹" },
    { key: "conv_rate", label: "Conversion Rate" },
    { key: "team_rank", label: "Team Rank" },
  ],
};

// ---------- Widget configs (stub identifiers; dashboard.tsx renders by key) ----------

export type WidgetKey =
  | "latest_submissions"
  | "other_tasks"
  | "output_chart"
  | "quick_submit"
  | "recent_hires"
  | "upcoming_joinings"
  | "pending_documents"
  | "workspace_activity"
  | "pending_approvals_list"
  | "influencer_funnel"
  | "calling_recent";

export const widgetConfig: Record<ScaffoldRole, WidgetKey[]> = {
  admin: ["workspace_activity", "pending_approvals_list"],
  hr: ["recent_hires", "upcoming_joinings", "pending_documents"],
  editor: ["latest_submissions", "output_chart", "quick_submit", "other_tasks"],
  graphic_designer: ["latest_submissions", "output_chart", "other_tasks"],
  ai: ["latest_submissions", "output_chart", "other_tasks"],
  scriptwriter: ["latest_submissions", "output_chart", "other_tasks"],
  marketer: ["latest_submissions", "other_tasks"],
  seo: ["latest_submissions", "other_tasks"],
  smm: ["influencer_funnel", "latest_submissions"],
  calling: ["calling_recent"],
};
